/**
 * Opens third-party HTTP(S) links in a new browsing context.
 *
 * Links can be rendered by Liquid or injected later by Shopify sections and apps,
 * so both the initial document and newly added markup are processed.
 */

const EXTERNAL_LINK_SELECTOR = 'a[href]';
const scriptElement = document.querySelector('#external-links-script');
const internalHostnames = new Set([
  window.location.hostname,
  ...(scriptElement?.dataset.shopDomains?.split(',') ?? []),
].filter(Boolean));

/**
 * @param {HTMLAnchorElement} link
 */
function applyExternalLinkPolicy(link) {
  let url;

  try {
    url = new URL(link.getAttribute('href') ?? '', window.location.href);
  } catch {
    return;
  }

  if (!['http:', 'https:'].includes(url.protocol) || internalHostnames.has(url.hostname)) return;

  link.target = '_blank';

  const relValues = new Set(link.rel.split(/\s+/).filter(Boolean));
  relValues.add('noopener');
  relValues.add('noreferrer');
  link.rel = [...relValues].join(' ');
}

/**
 * @param {ParentNode} root
 */
function processLinks(root) {
  if (root instanceof HTMLAnchorElement && root.matches(EXTERNAL_LINK_SELECTOR)) {
    applyExternalLinkPolicy(root);
  }

  root.querySelectorAll?.(EXTERNAL_LINK_SELECTOR).forEach(applyExternalLinkPolicy);
}

processLinks(document);

const externalLinkObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) processLinks(node);
    });
  }
});

externalLinkObserver.observe(document.documentElement, { childList: true, subtree: true });
